import { Stack, Typography, Grid, Button, Switch } from "@mui/material";
import ReactPhoneInput from 'react-phone-input-material-ui';

import update from "../../../api/auth/organization/update";
import useAuthStore from "../../../stores/useAuthStore";
import uploadFile from "../../../api/auth/organization/uploadFile";
import WushFileUploader from "../../../components/form/WushFileUploader";
import { useForm } from "react-hook-form";
import ColorPicker from "../../../components/form/ColorPicker";
import WushCard from "../../../components/layout/WushCard";
import ReactQuill from "react-quill";
import { useState } from "react";
import WushTextField from "../../../components/form/WushTextField";
import useAlertStore from "../../../stores/useAlertStore";
import WushStateSelect from "../../../components/form/WushStateSelect";

export default function Storefront() {
  const { handleSubmit, setValue, register, formState } = useForm();

  const { organization, setOrganization } = useAuthStore();
  const { setAlertMessage } = useAlertStore();

  const [ storefrontEnabled, setStorefrontEnabled ] = useState(organization.storefront_enabled);
  const [ showInventoryQuantities, setShowInventoryQuantities ] = useState(organization.storefront_display_inventory_quantities);

  const onSubmit = (data) => {
    update(organization.id, {
      storefront_enabled: storefrontEnabled,
      storefront_display_inventory_quantities: showInventoryQuantities,
      ...data
    }, (response) => {
      setOrganization(response);
      setAlertMessage("Storefront updated successfully", "success");
    });
  };

  const uploadImage = (file, attribute) => {
    uploadFile(organization.id, file, attribute, (response) => {
      setOrganization(response);
    });
  };

  return (
    <Stack>
      <Stack mb={2}>
        <Typography variant="h4">Storefront configuration</Typography>
      </Stack>
      <Stack>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <Stack direction="row" mb={2}>
                <Switch
                  checked={storefrontEnabled}
                  onChange={(event) => setStorefrontEnabled(event.target.checked)}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
                <Typography sx={{ my: "auto" }} variant="body1" fontWeight="700">
                  Storefront Enabled
                </Typography>
              </Stack>
              <Stack direction="row" mb={2}>
                <Switch
                  checked={showInventoryQuantities}
                  onChange={(event) => setShowInventoryQuantities(event.target.checked)}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
                <Typography sx={{ my: "auto" }} variant="body1">
                  Display inventory quantities on customer checkout
                </Typography>
              </Stack>
              <Stack mb={2}>
                <WushTextField formRegistration={register('homepage_url')} label="Homepage URL" defaultValue={organization?.homepage_url} />
              </Stack>
              <Stack mb={2}>
                <WushTextField formRegistration={register('admin_email')} label="Admin Email" defaultValue={organization?.admin_email} />
              </Stack>
              <Stack mb={2}>
                <WushTextField formRegistration={register('address_1')} label="Address 1" defaultValue={organization?.address_1} />
              </Stack>
              <Stack mb={2}>
                <WushTextField formRegistration={register('address_2')} label="Address 2" defaultValue={organization?.address_2} />
              </Stack>
              <Stack mb={2}>
                <WushTextField formRegistration={register('city')} label="City" defaultValue={organization?.city} />
              </Stack>
              <Stack mb={2}>
                <WushStateSelect formRegistration={register('state')} label="State" defaultValue={organization?.state} />
              </Stack>
              <Stack mb={2}>
                <WushTextField formRegistration={register('zip')} label="Zip" defaultValue={organization?.zip} />
              </Stack>
              <Stack mb={2}>
                <ReactPhoneInput
                  onChange={(phone) => setValue('support_phone', phone) }
                  defaultCountry={'us'}
                  country={'us'}
                  countryCodeEditable={true}
                  component={WushTextField}
                  withCountryCallingCode={true}
                  value={organization?.support_phone}
                  inputProps={{
                    formState: formState,
                  }}
                />
              </Stack>
              <Stack mb={2}>
                <Stack>
                  <Typography variant="h6">Storefront description</Typography>
                </Stack>
                <ReactQuill
                  theme="snow"
                  value={organization?.storefront_description}
                  onChange={(value) => setValue("storefront_description", value, { shouldDirty: true})}
                />
              </Stack>
              <Stack>
                <Stack direction="row" spacing={2} mb={2}>
                  <ColorPicker
                    initialValue={organization.storefront_header_background_color}
                    onChangeComplete={(color) => setValue("storefront_header_background_color", color, { shouldDirty: true})}
                  />
                  <Typography sx={{ my: "auto" }} variant="body1" fontWeight="700">
                    Storefront header color
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} mb={2}>
                  <ColorPicker
                    initialValue={organization.storefront_body_background_color}
                    onChangeComplete={(color) => setValue("storefront_body_background_color", color, { shouldDirty: true})}
                  />
                  <Typography sx={{ my: "auto" }} variant="body1" fontWeight="700">
                    Storefront background color
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} mb={2}>
                  <ColorPicker
                    initialValue={organization.storefront_header_font_color}
                    onChangeComplete={(color) => setValue("storefront_header_font_color", color, { shouldDirty: true})}
                  />
                  <Typography sx={{ my: "auto" }} variant="body1" fontWeight="700">
                    Storefront header font color
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row">
                <Button variant="contained" type="submit">Save</Button>
              </Stack>
            </Grid>
            <Grid item sm={6}>
              <Stack mb={2}>
                <Typography variant="h4">Storefront images</Typography>
              </Stack>
              <WushCard mb={2}>
                <Stack p={2}>
                  <Stack>
                    <Typography variant="body1" fontWeight="700">
                      Organization logo
                    </Typography>
                  </Stack>
                  {organization.organization_logo_url && <Stack mb={1}>
                    <img
                      style={{
                        borderRadius: "5px",
                        marginLeft: "auto",
                        marginRight: "auto"
                      }}
                      width="150px"
                      src={organization.organization_logo_url}
                      alt="Store Image"
                    />
                  </Stack>}
                  <Stack mb={2}>
                    <WushFileUploader handleFileUpload={(file) => uploadImage(file, 'organization_logo')} />
                  </Stack>
                </Stack>
              </WushCard>
              <WushCard>
                <Stack p={2}>
                  <Stack>
                    <Typography variant="body1" fontWeight="700">
                      Storefront primary image
                    </Typography>
                  </Stack>
                  {organization.storefront_image_url && <Stack mb={1}>
                    <img
                      style={{
                        borderRadius: "5px",
                        marginLeft: "auto",
                        marginRight: "auto"
                      }}
                      width="150px"
                      src={organization.storefront_image_url}
                      alt="Store Image"
                    />
                  </Stack>}
                  <Stack mb={2}>
                    <WushFileUploader handleFileUpload={(file) => uploadImage(file, 'storefront_image')} />
                  </Stack>
                </Stack>
              </WushCard>
            </Grid>
          </Grid>
        </form>
      </Stack>
    </Stack>
  );
}